import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable } from 'rxjs';

import { HelpInfo, HelpInfoService } from '@priva/appshell';
import { PrivaLocalizationService } from '@priva/localization';

import { absoluteRoutePaths } from 'app/core/routing';
import { Registration } from 'app/features/manual-input-registration/models';
import { MasterDataStorageService } from 'app/shared/services';
import { selectMasterData } from 'app/state/master-data/master-data.selectors';
import { selectCachedRegistrationsAsArray } from 'app/state/registrations/registrations.selectors';
import { selectIsUpdateAvailable } from 'app/state/service-worker/service-worker.selectors';
import { environment } from 'environments/environment';

@Injectable({
    providedIn: 'root',
})
export class AppHelpInfoService implements HelpInfoService {
    private privaLocalizationService = inject(PrivaLocalizationService);
    private masterDataStorageService = inject(MasterDataStorageService);
    private store = inject(Store);

    constructor() {
        this.helpInfo = combineLatest([
            this.store.select(selectCachedRegistrationsAsArray),
            this.store.select(selectIsUpdateAvailable),
            this.store.select(selectMasterData),
        ]).pipe(
            map(([cachedRegistrations, isUpdateAvailable, masterData]) => {
                return this.compileHelp({
                    cachedRegistrations,
                    isUpdateAvailable,
                    deviceName: masterData?.deviceName,
                    profileName: masterData?.deviceProfile?.name,
                });
            }),
        );
    }

    readonly helpInfo: Observable<HelpInfo>;

    private compileHelp({
        cachedRegistrations,
        isUpdateAvailable,
        deviceName,
        profileName,
    }: {
        cachedRegistrations: Registration[];
        isUpdateAvailable: boolean;
        deviceName: string | undefined;
        profileName: string | undefined;
    }): HelpInfo {
        const links = isUpdateAvailable
            ? [
                  {
                      title: 'SIDEBAR.HELP.ACTION.UPDATE_NOW',
                      uri: absoluteRoutePaths.update,
                  },
              ]
            : [];

        // Profile data is possibly null, therefore send empty title to hide data in Help section
        const profileData = profileName
            ? {
                  title: 'SIDEBAR.HELP.TITLE.PROFILE',
                  description: profileName,
              }
            : {
                  title: '',
              };

        return {
            about: {
                items: [
                    profileData,
                    {
                        title: `SIDEBAR.HELP.TITLE.DEVICE_NAME`,
                        // It is possible that deviceName exists but contains an empty string
                        // Therefore, if there's no deviceName set or empty, deviceId will be shown instead
                        description:
                            deviceName || this.masterDataStorageService.getDeviceNameFromMasterDataStore(),
                    },
                    {
                        title: 'SIDEBAR.HELP.TITLE.APPLICATION_VERSION',
                        description: environment?.configuration?.appVersion ?? '0.0.0',
                        links,
                    },
                    {
                        title: 'SIDEBAR.HELP.TITLE.STATUS',
                        description: cachedRegistrations.length
                            ? this.privaLocalizationService.instant(
                                  'SIDEBAR.HELP.DESCRIPTION.WARNING_CACHED_REGISTRATIONS',
                                  { cachedRegistrations: cachedRegistrations.length },
                              )
                            : 'SIDEBAR.HELP.DESCRIPTION.OK',
                    },
                ],
            },
        };
    }
}
